import type {
  NudgeResponse,
  ReminderRepeat,
  ReminderResponse,
  RemindersResponse,
} from '@nargus/shared';
import { download, request, upload } from '../http';

export interface CreateReminderInput {
  organizationId: string;
  title: string;
  note?: string;
  dueAt?: string;
  repeat?: ReminderRepeat;
  assigneeId?: string;
}

export interface UpdateReminderInput {
  organizationId: string;
  reminderId: string;
  title?: string;
  note?: string | null;
  dueAt?: string | null;
  repeat?: ReminderRepeat | null;
  assigneeId?: string | null;
  done?: boolean;
}

export interface ReminderRef {
  organizationId: string;
  reminderId: string;
}

export interface AttachmentUploadInput extends ReminderRef {
  file: File;
}

export interface AttachmentRef extends ReminderRef {
  attachmentId: string;
}

export interface NudgeReminderInput extends ReminderRef {
  userId: string;
}

export const reminderResource = {
  list: (organizationId: string): Promise<RemindersResponse> =>
    request(`/organizations/${organizationId}/reminders`),

  create: ({ organizationId, ...body }: CreateReminderInput): Promise<ReminderResponse> =>
    request(`/organizations/${organizationId}/reminders`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  update: ({
    organizationId,
    reminderId,
    ...body
  }: UpdateReminderInput): Promise<ReminderResponse> =>
    request(`/organizations/${organizationId}/reminders/${reminderId}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  nudge: ({
    organizationId,
    reminderId,
    userId,
  }: NudgeReminderInput): Promise<NudgeResponse> =>
    request(`/organizations/${organizationId}/reminders/${reminderId}/nudge`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    }),

  remove: ({ organizationId, reminderId }: ReminderRef): Promise<void> =>
    request(`/organizations/${organizationId}/reminders/${reminderId}`, {
      method: 'DELETE',
    }),

  addAttachment: ({
    organizationId,
    reminderId,
    file,
  }: AttachmentUploadInput): Promise<ReminderResponse> => {
    const body = new FormData();
    body.append('file', file);
    return upload(
      `/organizations/${organizationId}/reminders/${reminderId}/attachments`,
      body,
    );
  },

  getAttachment: ({
    organizationId,
    reminderId,
    attachmentId,
  }: AttachmentRef): Promise<Blob> =>
    download(
      `/organizations/${organizationId}/reminders/${reminderId}/attachments/${attachmentId}`,
    ),

  removeAttachment: ({
    organizationId,
    reminderId,
    attachmentId,
  }: AttachmentRef): Promise<void> =>
    request(
      `/organizations/${organizationId}/reminders/${reminderId}/attachments/${attachmentId}`,
      { method: 'DELETE' },
    ),
};
